import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '@visionar/shared';
import { authApi, setAuthToken } from '@/lib/api-client';

interface AuthState {
  user: User | null;
  sessionToken: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;

  // Actions
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  setUser: (user: User | null) => void;
  initializeAuth: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      sessionToken: null,
      isLoading: true,
      isAuthenticated: false,

      // Initialize auth token from persisted state
      initializeAuth: () => {
        const { sessionToken } = get();
        if (sessionToken) {
          setAuthToken(sessionToken);
        }
        set({ isLoading: false });
      },

      login: async (email, password) => {
        set({ isLoading: true });
        try {
          const response = await authApi.login({ email, password });
          const token = response.session?.id || null;
          if (token) {
            setAuthToken(token);
          }
          set({
            user: response.user,
            sessionToken: token,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      register: async (email, password, name) => {
        set({ isLoading: true });
        try {
          const response = await authApi.register({ email, password, name });
          const token = response.session?.id || null;
          if (token) {
            setAuthToken(token);
          }
          set({
            user: response.user,
            sessionToken: token,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      logout: async () => {
        set({ isLoading: true });
        try {
          await authApi.logout();
        } catch {
          // Ignore errors on logout
        }
        setAuthToken(null);
        set({
          user: null,
          sessionToken: null,
          isAuthenticated: false,
          isLoading: false,
        });
      },

      checkAuth: async () => {
        const { sessionToken, user } = get();

        // If we have persisted user and token, trust it (avoid unnecessary API call)
        if (sessionToken && user) {
          setAuthToken(sessionToken);
          set({ isLoading: false, isAuthenticated: true });
          return;
        }

        set({ isLoading: true });
        try {
          const response = await authApi.me();
          set({
            user: response.user,
            isAuthenticated: !!response.user,
            isLoading: false,
          });
        } catch {
          set({
            user: null,
            sessionToken: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      },

      setUser: (user) => {
        set({
          user,
          isAuthenticated: !!user,
        });
      },
    }),
    {
      name: 'visionar-auth',
      partialize: (state) => ({
        user: state.user,
        sessionToken: state.sessionToken,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrate: () => {
        // Called when store is rehydrated from localStorage
        return (state) => {
          if (state?.sessionToken) {
            setAuthToken(state.sessionToken);
          }
        };
      },
    }
  )
);
